import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api } from "@/lib/kfmApi";
import { BookOpen, Loader2, MapPin, Users } from "lucide-react";
import PronounceButton from "@/components/PronounceButton";

// A tappable Strong's citation → opens a dialog with the REAL, sourced dictionary
// entry (Pillar 1: Visible Sourcing) + Word Trail occurrences (Pillar 3), pulled
// from deterministic data (openscriptures + STEPBible), never AI-guessed.
export default function ConcordanceDialog({ number, label, entry, className = "", testid, sessionId }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const res = await api.get(`/concordance/${encodeURIComponent(number)}`);
      setData(res.data);
      // Record the encounter so it powers cross-session continuity (idempotent server-side).
      if (sessionId) {
        api.post(`/study/sessions/${sessionId}/word-seen`, {
          strongs_number: number, word: res.data.translit || res.data.lemma || number,
        }).catch(() => {});
      }
    } catch (e) {
      setErr("No sourced entry found for this word.");
    }
    setLoading(false);
  };

  const onOpen = (v) => {
    setOpen(v);
    if (v && !data) load();
  };

  return (
    <>
      <button
        type="button"
        data-testid={testid || `concordance-open-${number}`}
        onClick={() => onOpen(true)}
        className={`inline-flex items-center gap-1 rounded-full bg-[#10222A] hover:bg-[#10222A] text-[#00B8CC] border border-[#00E5FF]/20 px-2.5 py-0.5 text-xs font-medium transition-colors active:scale-95 ${className}`}
        title="View the sourced Strong's entry"
      >
        <BookOpen className="w-3.5 h-3.5" strokeWidth={1.75} />
        {label || number}
      </button>

      <Dialog open={open} onOpenChange={onOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="concordance-dialog">
          <DialogHeader>
            <DialogTitle className="text-left flex items-center gap-2">
              <span className="text-xs font-medium uppercase tracking-widest text-[#00E5FF]">Strong's {number}</span>
            </DialogTitle>
            <DialogDescription className="sr-only">
              The sourced Strong's concordance entry and Word Trail occurrences for {number}.
            </DialogDescription>
          </DialogHeader>

          {loading && (
            <div className="py-10 flex items-center justify-center text-slate-500">
              <Loader2 className="w-5 h-5 animate-spin" />
            </div>
          )}
          {err && <p className="text-sm text-slate-400 py-6 text-center">{err}</p>}

          {data && (
            <div className="space-y-5">
              <div className="flex items-end gap-3 flex-wrap">
                <span className="text-4xl text-white" dir={data.lang === "hebrew" ? "rtl" : "ltr"}>
                  {data.lemma}
                </span>
                <div className="flex flex-col">
                  {data.translit && <span className="text-lg text-slate-300 italic">{data.translit}</span>}
                  {data.pron && <span className="text-xs text-slate-500">/{data.pron}/</span>}
                </div>
                <PronounceButton
                  entry={{ strongs_number: number, transliteration: data.translit, original: data.lemma, word: data.translit }}
                />
              </div>

              {data.definition && (
                <div>
                  <div className="text-xs font-medium uppercase tracking-widest text-slate-500 mb-1">Definition</div>
                  <p className="text-base text-slate-300 leading-relaxed">{data.definition}</p>
                </div>
              )}
              {data.kjv_usage && (
                <div>
                  <div className="text-xs font-medium uppercase tracking-widest text-slate-500 mb-1">Rendered in the KJV as</div>
                  <p className="text-sm text-slate-400 leading-relaxed">{data.kjv_usage}</p>
                </div>
              )}
              {data.derivation && (
                <p className="text-xs text-slate-500 leading-relaxed">{data.derivation}</p>
              )}

              {/* Word Trail — where this exact word appears (Pillar 3) */}
              {data.occurrences?.count > 0 && (
                <div className="rounded-2xl bg-[#10222A] border border-[#00E5FF]/10 p-4" data-testid="word-trail">
                  <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-widest text-[#00E5FF] mb-2">
                    <MapPin className="w-4 h-4" strokeWidth={1.75} /> Word Trail
                  </div>
                  <p className="text-sm text-slate-400 mb-3">
                    Appears <span className="font-semibold text-slate-200">{data.occurrences.count.toLocaleString()}</span>{" "}
                    time{data.occurrences.count === 1 ? "" : "s"} in Scripture.
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {data.occurrences.refs.map((r, i) => (
                      <span key={i} className="text-[11px] rounded-full bg-[#0E131F] border border-[#29344D] px-2 py-0.5 text-slate-400">
                        {r}
                      </span>
                    ))}
                    {data.occurrences.count > data.occurrences.refs.length && (
                      <span className="text-[11px] text-slate-500 px-1 py-0.5">
                        +{(data.occurrences.count - data.occurrences.refs.length).toLocaleString()} more
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Author fingerprint — where this word is emphasised across the canon (Pillar 3) */}
              {data.author_fingerprint?.by_author?.length > 0 && (
                <div className="rounded-2xl bg-[#0E131F] border border-[#1E273D] border-[#29344D] p-4" data-testid="author-fingerprint">
                  <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-widest text-[#00E5FF] mb-3">
                    <Users className="w-4 h-4" strokeWidth={1.75} /> Author fingerprint
                  </div>
                  <div className="space-y-2">
                    {data.author_fingerprint.by_author.map((a, i) => {
                      const max = data.author_fingerprint.by_author[0].count || 1;
                      return (
                        <div key={i} className="flex items-center gap-3">
                          <span className="text-xs text-slate-400 w-32 shrink-0 truncate">{a.author}</span>
                          <span className="flex-1 h-2 rounded-full bg-[#141B29] overflow-hidden">
                            <span className="block h-full bg-[#00E5FF]" style={{ width: `${Math.max(6, (a.count / max) * 100)}%` }} />
                          </span>
                          <span className="text-xs text-slate-500 w-8 text-right">{a.count}</span>
                        </div>
                      );
                    })}
                  </div>
                  <p className="text-[11px] text-slate-500 mt-2">
                    Emphasis across a sample of {data.author_fingerprint.sampled} of {data.author_fingerprint.total.toLocaleString()} occurrences.
                  </p>
                </div>
              )}

              <p className="text-[11px] text-slate-500 pt-1 border-t border-[#1E273D]">
                Sourced from Strong's Concordance (openscriptures) and STEPBible tagged texts — not AI-generated.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
